import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import GoalConfigItem from "./GoalConfigItem";
import ErrorBoundary from "../common/ErrorBoundary";
import { updateGoal } from "../../../store/slices/admin/goals";
import { useParams } from "react-router-dom";
import moment from "moment";
import { fetchGoalConfiguration } from "../../../store/slices/admin/goalConfiguration";

const CreateGoal = () => {
	const dispatch = useDispatch();
	const { id } = useParams() as { id: string };
	useEffect(() => {
		dispatch(fetchGoalConfiguration(id));
	}, [dispatch, id]);
	
	const [renameActive, setRenameActive] = useState(false);
	const isLoading = useSelector((state: any) => state.goalConfiguration.isLoading);
	const isUpdating = useSelector((state: any) => state.goals.isUpdating);
	const [data, setData] = useState({
		title: "",
		metricType: "",
		target: "",
		startDate: "",
		endDate: "",
		startTime: "",
		endTime: "",
		storyType: "all",
		recurrencyType: "",
		sections: [],
		displayTimeleft: true,
		dailyRecurDays: { mon: true, tue: true, wed: true, thu: true, fri: true, sat: true, sun: true },
		weeklyStartDay: "mon",
		weeklyEndDay: "sun",
		monthlyStartDay: 1,
		monthlyEndDay: 31,
		monthlyWhole: true,
		neverending: true,
		chartType: "donut",
		lineChartAutoRotate: true,
	}) as any;
	const goalData = useSelector((state: any) => state.goalConfiguration.data);
	const brandData = useSelector((state: any) => state.authenticateuser.data);

	useEffect(() => {
		if (!isLoading && goalData) {
			const now = moment().utc();
			const neverending = moment(goalData.endDate).diff(now, "y") > 50;
			setData({
				title: goalData.title,
				metricType: goalData.metricType,
				target: goalData.target,
				startDate: goalData.startDate.split("T")[0],
				endDate: goalData.endDate.split("T")[0],
				startTime: goalData.startTime.slice(0, 5),
				endTime: goalData.endTime.slice(0, 5),
				defaultTimeWindow: goalData.defaultTimeWindow,
				storyType: goalData.storyType,
				recurrencyType: goalData.recurrencyType,
				sections: goalData.sections ?? [],
				conversions: goalData.conversions ?? [],
				displayTimeleft: !!goalData.displayTimeleft,
				dailyRecurDays: {
					mon: goalData.recurrencyValue?.includes("mon"),
					tue: goalData.recurrencyValue?.includes("tue"),
					wed: goalData.recurrencyValue?.includes("wed"),
					thu: goalData.recurrencyValue?.includes("thu"),
					fri: goalData.recurrencyValue?.includes("fri"),
					sat: goalData.recurrencyValue?.includes("sat"),
					sun: goalData.recurrencyValue?.includes("sun"),
				},
				weeklyStartDay: goalData.startDay,
				weeklyEndDay: goalData.endDay,
				monthlyStartDay: goalData.startDay,
				monthlyEndDay: goalData.endDay,
				monthlyWhole: goalData.startDay === 1 && goalData.endDay === 31,
				neverending,
				chartType: goalData.chartType ?? "donut",
				lineChartAutoRotate: !!goalData.lineChartAutoRotate,
			});
		}
	}, [isLoading, goalData]);

	const toggleRenameField = () => {
		setRenameActive((a) => !a);
	};

	const changeName = (e: any) => {
		let tc = { ...data } as any;
		tc.title = e.target.value;
		setData(tc);
	};

	const updateData = (key: any, value: any) => {
		setData((d: any) => ({ ...d, [key]: value }));
	};

	const handleSaveGoal = () => {
		const d: any = { ...data };
		if (d.recurrencyType === "onetime") {
			delete d.neverending;
		}
		if (d.recurrencyType === "daily") {
			d.recurrencyValue = Object.entries(data.dailyRecurDays)
				.filter(([day, include]) => include)
				.map(([day]) => day)
				.join(",");
		}
		if (d.recurrencyType === "weekly") {
			d.startDay = d.weeklyStartDay;
			d.endDay = d.weeklyEndDay;
		}
		if (d.recurrencyType === "monthly") {
			if (d.monthlyWhole) {
				d.startDay = 1;
				d.endDay = 31;
			} else {
				d.startDay = d.monthlyStartDay;
				d.endDay = d.monthlyEndDay;
			}
		}

		// payload cleanup
		d.startTime += ":00";
		d.endTime += ":00";
		delete d.dailyRecurDays;
		delete d.weeklyStartDay;
		delete d.weeklyEndDay;
		delete d.monthlyStartDay;
		delete d.monthlyEndDay;
		delete d.monthlyWhole;
		if (d.neverending) delete d.endDate;
		delete d.neverending;
		if (d.chartType !== "line") delete d.lineChartAutoRotate;

		dispatch(updateGoal(id, d));
	};

	return (
		<div className="container create-dashboard">
			<div className="row">
				{isLoading ? (
					<h2>Loading...</h2>
				) : (
					<>
						<div className="col-md-12">
							<div className="create-dashboard__header">
								{renameActive ? (
									<>
										<input
											type="text"
											className="form-control"
											value={data.title}
											onChange={changeName}
										/>
										<button className="btn btn-outline-primary" onClick={toggleRenameField}>
											Save
										</button>
									</>
								) : (
									<>
										<h2>{data.title}</h2>
										<button className="btn btn-outline-primary" onClick={toggleRenameField}>
											Rename
										</button>
									</>
								)}
								<div className="ml-auto">
									<button className="btn btn-primary" onClick={handleSaveGoal} disabled={isUpdating}>
										Save Goal
									</button>
								</div>
							</div>
						</div>
						<div className="col-md-12">
							<div className="config-tab d-block">
								<div className="configuration mt-5">
									<h2 className="mb-4">Edit Goal Configuration</h2>

									<div className="config-list">
										<ErrorBoundary>
											<GoalConfigItem
												id={0}
												isEdit={true}
												data={data}
												updateData={updateData}
												timezone={brandData?.domainSettings?.timezone}
											/>
										</ErrorBoundary>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default CreateGoal;
